import DetailSynopsis from './DetailSynopsis';
import { CollectionPicker } from './MovieCollections';
import { FranchiseGuide, AfterWatchDiscovery } from './MovieDiscovery';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { Button } from "react-bootstrap";
import Review from "./contentnav";
import { Tabs, Tab } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import CastList from "./cast";
import { PosterCard } from "./Catalog";
import Api_key from "./apiconfiguration";
import './moviedetails.css';
import DetailGlassLoading from './DetailGlassLoading';
import MovieChat from "./MovieChat";
import useLoadTransition from "./useLoadTransition";
import usePlayerControls from "./usePlayerControls";
import { FiX, FiPlay } from "react-icons/fi";
import preloadImage from "./preloadImage";
import { LikeButton, WatchlistButton } from "./LibraryActions";
import { useAuth } from "./AuthContext";
import MovieJournal from "./MovieJournal";
import { MoviePeople } from "./PeopleFeatures";
import CinematicInsights from "./CinematicInsights";
import useArtworkTheme from './useArtworkTheme';
import PlayerExtras from './PlayerExtras';
import useViewingSession from './useViewingSession';

const money = (value) => value
  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(value)
  : '—';

const runtimeLabel = (minutes) => {
  if (!minutes) return '—';
  const hours = Math.floor(minutes / 60);
  return hours ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
};

/** US theatrical certification, when TMDB has one. */
const certificationOf = (movie) => (movie['release_dates']?.results || [])
  .find(entry => entry.iso_3166_1 === 'US')?.release_dates
  ?.map(entry => entry.certification).find(Boolean) || null;

const MovieDetails = () => {
  const { id } = useParams();
  const [movie, setMovie] = useState(null);
  useArtworkTheme(movie?.backdrop_path || movie?.poster_path);
  const location = useLocation();
  const autoStarted = useRef(null);
  const [similarMovies, setSimilarMovies] = useState([]);
  const [showPlayer, setShowPlayer] = useState(false);
  const [isPlayerFullscreen, setIsPlayerFullscreen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const playerOverlayRef = useRef(null);
  const watchStartedAt = useRef(null);
  const { showLoader, isContentVisible } = useLoadTransition(!movie);
  const { controlsVisible, showControls } = usePlayerControls(showPlayer);
  const apiKey = Api_key;
  const { user, experience, saveExperienceItem } = useAuth();
  useViewingSession(showPlayer, movie, 'movie', playerOverlayRef);


  const handleWatchNow = useCallback(() => {
    if ("mediaSession" in navigator && window.MediaMetadata) {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: movie.title, artist: "Hedone", album: movie.genres?.map((genre) => genre.name).join(" · "),
        artwork: movie.poster_path ? [{src:`https://image.tmdb.org/t/p/w500${movie.poster_path}`,sizes:"500x750",type:"image/jpeg"}] : [],
      });
    }
    watchStartedAt.current = Date.now();
    if (user) {
      const previous = experience.progress?.[`movie_${id}`] || {};
      saveExperienceItem("progress", `movie_${id}`, {
        ...previous, id:Number(id), mediaType:"movie", title:movie.title,
        posterPath:movie.poster_path || null, backdropPath:movie.backdrop_path || null,
        durationSeconds:(movie.runtime || 0) * 60, progressSeconds:previous.progressSeconds || 0,
        lastWatchedAt:Date.now(),
      }).catch(() => {});
    }
    window.history.pushState({ ...window.history.state, hedonePlayer: true }, '');
    setShowPlayer(true);
  }, [movie, user, experience.progress, id, saveExperienceItem]);

  useEffect(() => {
    if (location.state?.watchNow && movie?.id === Number(id) && autoStarted.current !== location.key) {
      autoStarted.current = location.key;
      handleWatchNow();
    }
  }, [movie, id, location.state, location.key, handleWatchNow]);

  const persistWatchProgress = () => {
    if (!user || !watchStartedAt.current || !movie) return;
    const elapsed = Math.max(0, Math.round((Date.now() - watchStartedAt.current) / 1000));
    const previous = experience.progress?.[`movie_${id}`] || {};
    const durationSeconds = (movie.runtime || 0) * 60;
    const progressSeconds = durationSeconds
      ? Math.min(Math.round(durationSeconds * .96), (previous.progressSeconds || 0) + elapsed)
      : (previous.progressSeconds || 0) + elapsed;
    saveExperienceItem("progress", `movie_${id}`, {
      ...previous, id:Number(id), mediaType:"movie", title:movie.title,
      posterPath:movie.poster_path || null, backdropPath:movie.backdrop_path || null,
      durationSeconds, progressSeconds, lastWatchedAt:Date.now(),
      completed:durationSeconds > 0 && progressSeconds / durationSeconds >= .9,
    }).catch(() => {});
    watchStartedAt.current = null;
  };

  const closePlayer = () => {
    persistWatchProgress();
    if ("mediaSession" in navigator) navigator.mediaSession.metadata = null;
    const exitFullscreen =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.msExitFullscreen;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      Promise.resolve(exitFullscreen?.call(document)).catch(() => {});
    }
    if (window.history.state?.hedonePlayer) window.history.back();
    else setShowPlayer(false);
  };

  const togglePlayerFullscreen = async () => {
    try {
      const fullscreenElement =
        document.fullscreenElement || document.webkitFullscreenElement;
      if (fullscreenElement) {
        const exitFullscreen =
          document.exitFullscreen ||
          document.webkitExitFullscreen ||
          document.msExitFullscreen;
        await Promise.resolve(exitFullscreen?.call(document));
      } else {
        const player = playerOverlayRef.current;
        const requestFullscreen =
          player?.requestFullscreen ||
          player?.webkitRequestFullscreen ||
          player?.msRequestFullscreen;
        await Promise.resolve(requestFullscreen?.call(player));
      }
    } catch (error) {
      console.error("Unable to change fullscreen mode", error);
    }
  };

  useEffect(() => {
    const handlePlayerBack = () => setShowPlayer(false);
    window.addEventListener('popstate', handlePlayerBack);
    return () => window.removeEventListener('popstate', handlePlayerBack);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement || document.webkitFullscreenElement;
      setIsPlayerFullscreen(fullscreenElement === playerOverlayRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  useEffect(() => {
    if (!showPlayer) return undefined;
    const scrollY = window.scrollY;
    document.body.classList.add('player-open');
    document.body.style.top = `-${scrollY}px`;
    return () => {
      document.body.classList.remove('player-open');
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    };
  }, [showPlayer]);

  useEffect(() => {
    const fetchMovieDetails = async () => {
      try {
        const movieEndpoint = `https://api.themoviedb.org/3/movie/${id}?api_key=${apiKey}&append_to_response=credits,release_dates`;
        const similarMoviesEndpoint = `https://api.themoviedb.org/3/movie/${id}/similar?api_key=${apiKey}`;
        const imagesEndpoint = `https://api.themoviedb.org/3/movie/${id}/images?api_key=${apiKey}`;

        const [movieResponse, imagesResponse, ...responses] = await Promise.all([
          axios.get(movieEndpoint),
          axios.get(imagesEndpoint),
          axios.get(`${similarMoviesEndpoint}&page=1`),
          axios.get(`${similarMoviesEndpoint}&page=2`),
          axios.get(`${similarMoviesEndpoint}&page=3`),
          axios.get(`${similarMoviesEndpoint}&page=4`),
          axios.get(`${similarMoviesEndpoint}&page=5`),
          axios.get(`${similarMoviesEndpoint}&page=6`),
          axios.get(`${similarMoviesEndpoint}&page=7`),
          axios.get(`${similarMoviesEndpoint}&page=8`),
        ]);

        const logos = imagesResponse.data.logos || [];
        const enLogo = logos.find(logo => logo.iso_639_1 === 'en');
        setLogoUrl(enLogo ? `https://image.tmdb.org/t/p/original${enLogo.file_path}` : null);

        const combinedMovies = responses.flatMap((response) => response.data.results);

        await preloadImage(
          movieResponse.data.backdrop_path
            ? `https://image.tmdb.org/t/p/original${movieResponse.data.backdrop_path}`
            : null
        );
        setMovie(movieResponse.data);
        setSimilarMovies(combinedMovies.slice(0, 120));
      } catch (error) {
        console.error(error);
      }
    };

    fetchMovieDetails();
  }, [apiKey, id]);

  useEffect(() => {
    if (!movie || location.hash !== '#movie-journal') return;
    const timer = setTimeout(() => document.getElementById('movie-journal')?.scrollIntoView({ block: 'start' }), 400);
    return () => clearTimeout(timer);
  }, [movie, location.hash]);

  if (!movie) {
    return (
      <div className="page-loader-layer">
        <DetailGlassLoading type='movie' />
      </div>
    );
  }

  const credits = movie.credits || {};
  const director = (credits.crew || []).find(person => person.job === 'Director')?.name;
  const writers = (credits.crew || [])
    .filter(person => ['Screenplay', 'Writer', 'Story'].includes(person.job))
    .slice(0, 2).map(person => person.name).join(' · ');
  const topCast = (credits.cast || []).slice(0, 4).map(person => person.name).join(' · ');
  const certification = certificationOf(movie);

  return (
    <div className="page-stage movie-detail-page">
    {showLoader && (
      <div className="page-loader-layer is-exiting">
        <DetailGlassLoading type='movie' />
      </div>
    )}
      <div className={`page-reveal ${isContentVisible ? 'is-visible' : ''}`} >
        <div
          style={{
            height: "650px",
            backgroundImage: `url(https://image.tmdb.org/t/p/original${movie.backdrop_path})`,
            backgroundSize: "cover",
            backgroundPosition: "",
            position: "relative",
          }}
          className="cover-image"
        >
          <div
            style={{
              position: "absolute",
              bottom: 0,
              width: "100%",
              height: "60%",
              background: "linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent)",
            }}
          ></div>
          <div className="movie-hero-content">
          <span className="detail-eyebrow">HEDONE CINEMA <i /> {movie.status || 'MOVIE'}</span>
          <h1 className="movie-title">{logoUrl ? <img src={logoUrl} onError={() => setLogoUrl(null)} alt={movie.title} /> : movie.title}</h1>
          {movie.tagline && <p className="movie-tagline">{movie.tagline}</p>}
          <div className="movie-facts">
            <span className="audience-score">{movie.vote_average > 0 ? `${movie.vote_average.toFixed(1)} / 10` : 'Not rated'}<small>TMDB</small></span>
            <span>{movie.release_date?.slice(0, 4)}</span>
            {certification && <span className="cert-badge">{certification}</span>}
            <span>{runtimeLabel(movie.runtime)}</span>
            
            <span>{movie.genres?.slice(0, 3).map(genre => genre.name).join(' · ')}</span>
          </div>
          <DetailSynopsis key={movie.id} text={movie.overview} className="movie-overview" />
          {(director || writers || topCast) && (
            <dl className="movie-credits">
              {director && <div><dt>Director</dt><dd>{director}</dd></div>}
              {writers && <div><dt>Writing</dt><dd>{writers}</dd></div>}
              {topCast && <div><dt>Starring</dt><dd>{topCast}</dd></div>}
            </dl>
          )}
          <div className="watch" style={{  }}>
            <Button variant="dark" className="hero-watch-btn" onClick={handleWatchNow}>
              <FiPlay aria-hidden="true" /> Watch now
            </Button>
            <div className="detail-library-actions">
              <WatchlistButton item={movie} mediaType="movie" />
              <LikeButton item={movie} mediaType="movie" />
            </div>
          </div>
          </div>
          {showPlayer && createPortal(
            <div
              className={`video-player-overlay ${controlsVisible ? "controls-visible" : "controls-hidden"}`}
              ref={playerOverlayRef}
              onPointerMove={showControls}
            >
              <Button
                variant="light"
                className="video-player-close"
                aria-label="Close player"
                onClick={closePlayer}
              >
                <FiX aria-hidden="true" /><span>Close</span>
              </Button>
              <Button
                variant="light"
                className={`video-player-expand ${isPlayerFullscreen ? "is-fullscreen" : ""}`}
                onClick={togglePlayerFullscreen}
                aria-label={
                  isPlayerFullscreen ? "Exit full screen" : "Enter full screen"
                }
                title={
                  isPlayerFullscreen ? "Exit full screen" : "Full screen"
                }
              >
                {isPlayerFullscreen ? "×" : "⛶"}
              </Button>
              <iframe
                src={`https://vidsrc.sh/embed/movie?tmdb=${movie.id}&autoplay=1`}
                width="100%"
                height="100%"
                allow="autoplay; encrypted-media; picture-in-picture; web-share"
                frameBorder="0"
                allowFullScreen
                title="Movie Player"
                className="video-player-frame"
                onMouseEnter={showControls}
              />
              <MovieChat
                movieId={movie.id}
                movieTitle={movie.title}
                mode="player"
                playerControlsVisible={controlsVisible}
              />
              <PlayerExtras item={movie} playerRef={playerOverlayRef}/>
            </div>,
            document.body
          )}
        </div>
        <Container className="my-4 text-white" style={{ position: 'relative', zIndex: 1 }}>
          <Row className="detail-tabs-row">
            <Tabs
              defaultActiveKey="details"
              id="account-tabs"
              className="detail-tabs"
              fill
              justify
            >
              <Tab eventKey="account" title="Reviews">
                <Review id={movie.id} />
              </Tab>
              <Tab eventKey="details" title="Movie Details">
                <div className="metadata-panel">
                  <div><span>Release date</span><strong>{movie.release_date || 'Not announced'}</strong></div>
                  <div><span>Audience score</span><strong>{movie.vote_average?.toFixed(1)} / 10 <em>({movie.vote_count?.toLocaleString()} votes)</em></strong></div>
                  <div><span>Runtime</span><strong>{movie.runtime ? `${movie.runtime} minutes` : '—'}</strong></div>
                  <div><span>Status</span><strong>{movie.status || '—'}</strong></div>
                  <div><span>Rated</span><strong>{certification || 'Not rated'}</strong></div>
                  <div><span>Original title</span><strong>{movie.original_title || movie.title}</strong></div>
                  <div><span>Budget</span><strong>{money(movie.budget)}</strong></div>
                  <div><span>Box office</span><strong>{money(movie.revenue)}</strong></div>
                  <div className="wide"><span>Genres</span><strong>{movie.genres.map((genre) => genre.name).join(" · ")}</strong></div>
                  <div className="wide"><span>Spoken languages</span><strong>{movie.spoken_languages?.map(language => language.english_name).join(" · ") || movie.original_language?.toUpperCase()}</strong></div>
                  <div className="wide"><span>Production</span><strong>{movie.production_companies?.map(company => company.name).join(" · ") || '—'}</strong></div>
                  <div className="wide"><span>Countries</span><strong>{movie.production_countries?.map(country => country.name).join(" · ") || '—'}</strong></div>
                </div>
              </Tab>
              <Tab eventKey="Cast" title="Cast & Crew">
                <CastList id={movie.id} />
              </Tab>
            </Tabs>
          </Row>

          <CollectionPicker key={`collection_${user?.uid || "guest"}_${movie.id}`} item={movie} />
          <FranchiseGuide key={`franchise_${user?.uid || "guest"}_${movie.belongs_to_collection?.id}`} movie={movie} />
          <AfterWatchDiscovery key={movie.id} movie={movie} />
          <MovieJournal key={`${user?.uid || "guest"}_${movie.id}`} movie={movie} />
          <MoviePeople id={movie.id} />
          <CinematicInsights mediaType="movie" id={movie.id} item={movie} />

          <section className="similar-rail" aria-labelledby="similar-movies">
            <div className="collection-heading">
              <div>
                <span className="section-kicker">IF YOU LIKED THIS</span>
                <h2 id="similar-movies">Similar movies</h2>
              </div>
            </div>
            <div className="poster-rail">
              {similarMovies.slice(0, 24).map((similar) => (
                <PosterCard key={similar.id} item={similar} mediaType="movie" />
              ))}
            </div>
          </section>
        </Container>
        {!showPlayer && (
          <MovieChat movieId={movie.id} movieTitle={movie.title} />
        )}
      </div>
    </div>
  );
};

export default MovieDetails;
